import { TableCellWithBadge } from "../../../../data-model/tableCells";
import { PharmaStpFileModel } from "../../../../data-model/nycbsPharmaOrder";
import { PaStatus } from "../../../../enums/paStatus";

/** Yellow badge styling for an initiated biosimilar state. */
const INITIATED_BADGE = {
  text: "Initiated",
  color: "#665D00",
  bgColor: "#FFFCD6",
} as const;

/** Grey badge styling for a null biosimilar state. */
const NULL_BADGE = {
  text: "Null",
  color: "#000000",
  bgColor: "#F5F5F5",
} as const;

/** Green badge styling when a parent row has a source drug. */
const YES_BADGE = {
  text: "Yes",
  color: "#005D49",
  bgColor: "#E6F3F0",
} as const;

/**
 * Returns whether a string field from the API should be treated as present.
 *
 * @param value - Raw string or nullish from the API
 * @return True when the value is meaningful for parent/source checks
 */
const hasMeaningfulString = (value: string | null | undefined): boolean => {
  if (value == null) {
    return false;
  }

  const trimmed = String(value).trim();
  return trimmed !== "" && trimmed.toLowerCase() !== "nan";
};

/**
 * Returns whether the row PA status is denied.
 *
 * @param responseStatus - Raw `response_status` from the API
 * @return True when the outcome is denied
 */
const isDeniedPaStatus = (
  responseStatus: string | null | undefined,
): boolean => {
  if (!responseStatus) {
    return false;
  }

  return responseStatus.trim().toLowerCase() === PaStatus.DENIED.toLowerCase();
};

/**
 * Builds the Biosimilar badge cell value for an Astera Oncology External row.
 *
 * @param item - Raw order row from the API
 * @return TableCellWithBadge: Badge label and colors for the Biosimilar column
 */
export const getAsteraBiosimilarBadgeValue = (
  item: PharmaStpFileModel,
): TableCellWithBadge => {
  const hasParentId = hasMeaningfulString(item.parent_identifier);
  const hasSourceDrug = hasMeaningfulString(item.source_drug);

  if (hasParentId && hasSourceDrug) {
    return YES_BADGE;
  }

  if (
    !hasParentId &&
    !hasSourceDrug &&
    item.is_biosimilar_case === true &&
    isDeniedPaStatus(item.response_status)
  ) {
    return INITIATED_BADGE;
  }

  return NULL_BADGE;
};

export { INITIATED_BADGE, NULL_BADGE, YES_BADGE };
